/**
 * RoutinesPage — NAV-3 (Fix-Wave-7)
 *
 * Minimal list view for Routines. Shows all org routines from the
 * cross-domain GET /api/v1/routines endpoint.
 *
 * Scope: list + read-only detail modal. No create/edit/delete — that is J5.
 * Sven's locked decision Q1=B: build the minimal page now rather than hiding.
 *
 * PRD §9.9 Routines.
 */

import React, { useState, useMemo } from 'react';
import { RorosPage } from '@/components/roros/RorosPage';
import { QueryPage } from '@/components/QueryPage';
import { Modal } from '@/components/ui/Modal/Modal';
import { Badge } from '@/components/ui/Badge';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Switch } from '@/components/ui/Switch';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { showToast } from '@/components/ui';
import { RefreshCw, Sparkles, Play, Trash2 } from 'lucide-react';
import { t } from '@/lib/i18n';
import { useRorosRoutines, useRorosRoutineArtifactUpdate, useRorosRoutineArtifactArchive } from '@/hooks/roros/artifacts';
import { useRorosAgentExecute } from '@/hooks/roros/agents';
import { useRorosDomains } from '@/hooks/roros/domains';
import { useRorosActors } from '@/hooks/roros/actors';
import { usePermissions } from '@/hooks/usePermissions';
import type { RorosRoutineArtifactDTO } from '@/types/roros/artifacts';
import type { RorosDomainDTO } from '@/types/roros/domains';
import type { RorosActorDTO } from '@/types/roros/actors';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function statusVariant(status: string): 'success' | 'warning' | 'default' {
  switch (status) {
    case 'active': return 'success';
    case 'archived': return 'default';
    default: return 'warning';
  }
}

function resolveDomainName(domains: RorosDomainDTO[], domainId: string): string {
  return domains.find((d) => d.id === domainId)?.name ?? domainId;
}

function resolveActorName(actors: RorosActorDTO[], actorId: string | null | undefined): string {
  if (!actorId) return '—';
  return actors.find((a) => a.id === actorId)?.name ?? actorId;
}

// ─── Detail Modal ─────────────────────────────────────────────────────────────

interface RoutineDetailModalProps {
  routine: RorosRoutineArtifactDTO;
  domains: RorosDomainDTO[];
  actors: RorosActorDTO[];
  onClose: () => void;
}

function RoutineDetailModal({ routine, domains, actors, onClose }: RoutineDetailModalProps) {
  const updateMutation = useRorosRoutineArtifactUpdate();
  // Phase 4 fix-3: "Run Routine" button. Wires the routine to the existing
  // operator-executor endpoint via the routine's owner_actor_id (the AI
  // Operator that owns it). Was missing — the execute endpoint and mutation
  // hook existed but no UI surface mounted them.
  const executeMutation = useRorosAgentExecute();
  // Phase 4 fix-4: capture the result inline so the user sees concrete
  // feedback without leaving the modal — toast alone wasn't visible enough.
  const [lastRunResult, setLastRunResult] = useState<{
    deliverable: string | null;
    qcReport: any | null;
    workItemId: string | null;
  } | null>(null);

  const handleSelfQcToggle = (enabled: boolean) => {
    updateMutation.mutate({
      domainId: routine.domainId,
      id: routine.id,
      payload: { enable_self_qc: enabled },
    });
  };

  const handleRunRoutine = () => {
    if (!routine.ownerActorId) {
      showToast.error(t('Cannot run: routine has no owner AI Operator assigned.'));
      return;
    }
    setLastRunResult(null);
    executeMutation.mutate(
      { actor_id: routine.ownerActorId, routine_id: routine.id, action: 'execute' },
      {
        onSuccess: (data: any) => {
          setLastRunResult({
            deliverable: data?.deliverable ?? null,
            qcReport: data?.qcReport ?? null,
            workItemId: data?.deliverableWorkItemId ?? null,
          });
          showToast.success(t('Routine executed. Check the Verification Inbox for the deliverable + QC report.'));
        },
        onError: (err: any) => {
          showToast.error(err?.message ?? t('Routine execution failed.'));
        },
      },
    );
  };

  return (
    <Modal isOpen onClose={onClose} title={routine.title} size="lg">
      <div className="space-y-4 text-sm">
        {/* Basic info */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">{t('Domain')}</p>
            <p className="font-medium">{resolveDomainName(domains, routine.domainId)}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">{t('Status')}</p>
            <Badge variant={statusVariant(routine.status)}>{t(routine.status)}</Badge>
          </div>
          <div>
            <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">{t('Trigger Type')}</p>
            <p className="font-medium">{routine.triggerType ? t(routine.triggerType) : '—'}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">{t('Owner')}</p>
            <p className="font-medium">{resolveActorName(actors, routine.ownerActorId)}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">{t('Maturity Stage')}</p>
            <p className="font-medium">{routine.maturityStage ?? '—'}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">{t('Max Iterations')}</p>
            <p className="font-medium">{routine.maxIterations}</p>
          </div>
        </div>

        {/* Description */}
        {routine.description && (
          <div>
            <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">{t('Description')}</p>
            <p className="text-foreground">{routine.description}</p>
          </div>
        )}

        {/* Evidence requirements — accepts both string[] AND {required: string[]} (PRD-spec'd shape).
            Phase 4 fix: routines seeded with the {required: [...]} object were silently invisible
            because length checks fail on objects; now we normalize first. */}
        {(() => {
          const er = routine.evidenceRequirements as any;
          const items: string[] = Array.isArray(er)
            ? er.map((r) => (typeof r === 'string' ? r : (r?.criterion || JSON.stringify(r))))
            : Array.isArray(er?.required)
              ? er.required
              : [];
          if (items.length === 0) return null;
          return (
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">{t('Evidence Requirements')}</p>
              <ul className="list-disc list-inside space-y-1">
                {items.map((r, i) => (
                  <li key={i} className="text-foreground">{r}</li>
                ))}
              </ul>
            </div>
          );
        })()}

        {/* Outputs — accepts both string[] AND structured [{type, kind, target_status}].
            Phase 4 fix: structured output objects were rendering as raw React children → crash. */}
        {routine.outputs && routine.outputs.length > 0 && (
          <div>
            <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">{t('Outputs')}</p>
            <ul className="list-disc list-inside space-y-1">
              {routine.outputs.map((o: any, i) => {
                const label = typeof o === 'string'
                  ? o
                  : (o?.kind || o?.type)
                    ? `${o?.kind ?? o?.type}${o?.target_status ? ` → ${String(o.target_status).replace(/_/g, ' ')}` : ''}`
                    : JSON.stringify(o);
                return (
                  <li key={i} className="text-foreground">{label}</li>
                );
              })}
            </ul>
          </div>
        )}

        {/* Value classification — accepts both string AND structured {value_tier} object.
            Phase 4 fix-3: object was being passed to t() → '[object Object]'. */}
        {(() => {
          const vc = routine.valueClassification as any;
          if (vc == null) return null;
          const label = typeof vc === 'string'
            ? t(vc)
            : (vc.value_tier ? t(String(vc.value_tier)) : null);
          if (!label) return null;
          return (
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">{t('Value Classification')}</p>
              <p className="font-medium capitalize">{label}</p>
            </div>
          );
        })()}

        {/* A.2 EO Connect Demo Phase 2 — Self-QC toggle */}
        <div className="pt-3 border-t border-border">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-1.5">
                <Sparkles className="h-4 w-4 text-primary-600" aria-hidden />
                <p className="text-sm font-medium text-foreground">{t('Enable AI self-quality-check')}</p>
              </div>
              <p className="text-xs text-muted-foreground mt-0.5">
                {t('Operator self-critiques output against evidence requirements after each execution.')}
              </p>
            </div>
            <Switch
              checked={routine.enableSelfQc ?? false}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleSelfQcToggle(e.target.checked)}
              aria-label={t('Enable AI self-quality-check')}
              disabled={updateMutation.isPending}
              data-testid="enable-self-qc-toggle"
            />
          </div>
        </div>

        {/* Phase 4 fix-3 — Run Routine button. Wires to the existing
            operator-executor via the routine's owner AI Operator. */}
        <div className="pt-3 border-t border-border flex items-center justify-between gap-3">
          <div className="text-xs text-muted-foreground">
            {routine.ownerActorId
              ? t('Manual execution. Result lands in Verification Inbox for CEO approval.')
              : t('No AI Operator owner assigned — assign one to run this routine.')}
          </div>
          <Button
            type="button"
            variant="primary"
            size="sm"
            onClick={handleRunRoutine}
            disabled={!routine.ownerActorId || executeMutation.isPending || routine.status !== 'active'}
            data-testid="run-routine-btn"
          >
            <Play className="h-4 w-4 mr-1.5" aria-hidden />
            {executeMutation.isPending ? t('Running…') : t('Run Routine')}
          </Button>
        </div>

        {/* Phase 4 fix-4 — inline run result so feedback is visible without
            leaving the modal (toast alone is too easy to miss for a 30-sec LLM call). */}
        {executeMutation.isPending && (
          <div className="rounded-md bg-primary-50 border border-primary-200 p-3 text-xs text-primary-700">
            {t('Operator is running. This may take 10-30 seconds (LLM call + self-QC if enabled)…')}
          </div>
        )}
        {/* Persistent error panel — toast alone disappears in a few seconds,
            which is too easy to miss when you've been staring at "Running…" for
            30 sec. The server now returns action-oriented messages
            (AGENT_DEFINITION_NOT_FOUND / ROROS_ROLE_CONTRACT_VIOLATION etc),
            so showing `error.message` verbatim tells the user exactly what
            to fix. */}
        {executeMutation.isError && !executeMutation.isPending && (
          <div
            className="rounded-md bg-error-50 border border-error-200 p-3 text-xs text-error-700"
            data-testid="run-routine-error"
            role="alert"
          >
            <p className="font-semibold mb-1">{t('Routine execution failed')}</p>
            <p className="whitespace-pre-wrap">
              {(executeMutation.error as any)?.message ?? t('Unknown error — try again.')}
            </p>
          </div>
        )}
        {lastRunResult && (
          <div className="rounded-md border border-success-200 bg-success-50 p-3 space-y-2">
            <div className="flex items-center gap-1.5">
              <Sparkles className="h-4 w-4 text-success-700" aria-hidden />
              <p className="text-sm font-semibold text-success-700">{t('Routine executed successfully')}</p>
            </div>
            {lastRunResult.deliverable && (
              <div>
                <p className="text-xs font-semibold text-foreground mb-1">{t('Deliverable')}</p>
                <p className="text-xs text-foreground whitespace-pre-wrap max-h-48 overflow-y-auto bg-white rounded p-2 border border-border">
                  {lastRunResult.deliverable}
                </p>
              </div>
            )}
            {lastRunResult.qcReport && (
              <div>
                <p className="text-xs font-semibold text-foreground mb-1">
                  {t('QC Report')} —{' '}
                  {lastRunResult.qcReport.finalPassed ? '✓ ' + t('All criteria passed') : '✗ ' + t('Some criteria failed')}
                  {lastRunResult.qcReport.iterations > 0 && ` · ${lastRunResult.qcReport.iterations} ${t('iteration(s)')}`}
                </p>
                <ul className="text-xs space-y-0.5">
                  {(lastRunResult.qcReport.criteria || []).map((c: any, i: number) => (
                    <li key={i} className={c.passed ? 'text-success-700' : 'text-error-700'}>
                      {c.passed ? '✓' : '✗'} {c.name}
                      {!c.passed && c.reason && <span className="text-muted-foreground"> — {c.reason}</span>}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {lastRunResult.workItemId && (
              <p className="text-xs text-muted-foreground">
                {t('Work item created in Verification Inbox.')}
              </p>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}

// ─── Routine Row ──────────────────────────────────────────────────────────────

interface RoutineRowProps {
  routine: RorosRoutineArtifactDTO;
  domains: RorosDomainDTO[];
  actors: RorosActorDTO[];
  onView: () => void;
  onDelete?: () => void;
  canDelete?: boolean;
}

function RoutineRow({ routine, domains, actors, onView, onDelete, canDelete }: RoutineRowProps) {
  return (
    <Card
      className="p-4 cursor-pointer hover:border-primary-300 transition-colors"
      role="row"
      tabIndex={0}
      aria-label={routine.title}
      onClick={onView}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onView(); }}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-medium text-sm truncate">{routine.title}</h3>
            <Badge variant={statusVariant(routine.status)}>{t(routine.status)}</Badge>
          </div>
          <div className="flex flex-wrap gap-3 mt-1.5 text-xs text-muted-foreground">
            <span>{t('Domain')}: <span className="font-medium text-foreground">{resolveDomainName(domains, routine.domainId)}</span></span>
            {routine.triggerType && (
              <span>{t('Trigger')}: <span className="font-medium text-foreground">{t(routine.triggerType)}</span></span>
            )}
            {routine.ownerActorId && (
              <span>{t('Owner')}: <span className="font-medium text-foreground">{resolveActorName(actors, routine.ownerActorId)}</span></span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => { e.stopPropagation(); onView(); }}
            aria-label={t('View details')}
          >
            {t('View')}
          </Button>
          {canDelete && routine.status !== 'archived' && (
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => { e.stopPropagation(); onDelete?.(); }}
              aria-label={t('Delete')}
              className="text-destructive hover:text-destructive"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export function RoutinesPage() {
  // Phase 4 fix-4: hold routine ID (not the snapshot) so the modal re-renders
  // with fresh data after mutations (toggle, execute) invalidate the query.
  const [selectedRoutineId, setSelectedRoutineId] = useState<string | null>(null);
  const [archiveTarget, setArchiveTarget] = useState<{ id: string; title: string } | null>(null);

  const routinesQuery = useRorosRoutines();
  const domainsQuery = useRorosDomains();
  const actorsQuery = useRorosActors();
  const archiveMutation = useRorosRoutineArtifactArchive();
  const { hasPermission } = usePermissions();
  const canDelete = hasPermission('roros.artifacts.delete');

  const domains = (domainsQuery.data ?? []) as RorosDomainDTO[];
  const actors = (actorsQuery.data ?? []) as RorosActorDTO[];

  const routines = useMemo(
    () => ((routinesQuery.data ?? []) as RorosRoutineArtifactDTO[]),
    [routinesQuery.data],
  );

  // Phase 4 fix-4: derive the selected routine from the LIVE query data on
  // every render so toggle / execute mutations that invalidate the query
  // surface fresh values inside the open modal (no need to close + reopen).
  const selectedRoutine = useMemo(
    () => (selectedRoutineId ? routines.find((r) => r.id === selectedRoutineId) ?? null : null),
    [selectedRoutineId, routines],
  );

  return (
    <RorosPage
      title={t('Routines')}
      icon={<RefreshCw className="w-5 h-5" />}
      breadcrumbs={[{ label: t('Routines'), path: '/roros/artifacts/routines' }]}
    >
      <QueryPage
        query={routinesQuery as any}
        emptyMessage={t('No routines defined yet')}
        emptyIcon={<RefreshCw className="h-8 w-8 text-muted-foreground" />}
      >
        {(_) => (
          <div className="space-y-3" role="table" aria-label={t('Routines')}>
            {routines.map((routine) => (
              <RoutineRow
                key={routine.id}
                routine={routine}
                domains={domains}
                actors={actors}
                onView={() => setSelectedRoutineId(routine.id)}
                onDelete={() => setArchiveTarget({ id: routine.id, title: routine.title })}
                canDelete={canDelete}
              />
            ))}
          </div>
        )}
      </QueryPage>

      {selectedRoutine && (
        <RoutineDetailModal
          routine={selectedRoutine}
          domains={domains}
          actors={actors}
          onClose={() => setSelectedRoutineId(null)}
        />
      )}

      <ConfirmDialog
        isOpen={!!archiveTarget}
        onClose={() => setArchiveTarget(null)}
        onConfirm={() => {
          if (!archiveTarget) return;
          archiveMutation.mutate(archiveTarget.id, {
            onSuccess: () => {
              showToast.success(t('Routine deleted'));
              setArchiveTarget(null);
            },
            onError: (err: any) =>
              showToast.error(err?.message ?? t('Failed to delete routine')),
          });
        }}
        title={t('Delete this routine?')}
        description={archiveTarget ? `"${archiveTarget.title}"` : ''}
        confirmText={t('Delete')}
        variant="danger"
        isLoading={archiveMutation.isPending}
      />
    </RorosPage>
  );
}
